/**
 * Organization Billing Section
 *
 * Admin view for managing an organization's billing configuration.
 * Provides full CRUD capabilities for Stedi connection, billing identities, and enrollments.
 *
 * Uses admin routes (/v2/admin/orgs/{organizationId}/*) for all operations.
 * Matches functionality of the provider BillingSettingsPage.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Add as AddIcon,
  CheckCircle as CheckCircleIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Error as ErrorIcon,
  PlayArrow as PlayArrowIcon,
  Refresh as RefreshIcon,
  Sync as SyncIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { useStediStatusAdmin, useStediConnectionAdmin } from '@features/stedi-connection';
import { useBillingEntitiesAdmin, useBillingEntityOperationsAdmin } from '@features/billing-entities';
import { useEnrollmentsAdmin, useEnrollmentOperationsAdmin } from '@features/enrollments';
import { useOrgReadinessAdmin } from '@features/billing-readiness';
import { SetupProgressIndicator } from '@features/billing-readiness/components/SetupProgressIndicator';
import { PayerSearchDropdown } from '@features/billing-readiness/components/PayerSearchDropdown';
import { EditBillingEntityModal } from '@features/billing-readiness/components/EditBillingEntityModal';
import { extractApiErrorMessage } from '@features/billing-readiness/utils/errorUtils';
import type { BillingEntityResponse } from '@store/generated/billing-entities';

type TabValue = 'overview' | 'identities' | 'enrollments';

interface TabPanelProps {
  children?: React.ReactNode;
  value: TabValue;
  activeTab: TabValue;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, activeTab }) => {
  if (value !== activeTab) {
    return null;
  }
  return <Box sx={{ pt: 3 }}>{children}</Box>;
};

// ============================================================================
// Form Data Types
// ============================================================================

interface BillingEntityFormData {
  billing_npi: string;
  tax_id: string;
  tax_id_type: string;
  legal_name: string;
  doing_business_as: string;
  billing_address: {
    line1: string;
    line2: string;
    city: string;
    state: string;
    zip: string;
  };
  contact_name: string;
  contact_email: string;
  contact_phone: string;
}

const initialFormData: BillingEntityFormData = {
  billing_npi: '',
  tax_id: '',
  tax_id_type: 'EIN',
  legal_name: '',
  doing_business_as: '',
  billing_address: {
    line1: '',
    line2: '',
    city: '',
    state: '',
    zip: '',
  },
  contact_name: '',
  contact_email: '',
  contact_phone: '',
};

// ============================================================================
// Main Component
// ============================================================================

interface OrganizationBillingSectionProps {
  organizationId: string;
}

export function OrganizationBillingSection({ organizationId }: OrganizationBillingSectionProps) {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();

  // Tab state
  const [currentTab, setCurrentTab] = useState<TabValue>('overview');

  // Dialog states
  const [connectDialogOpen, setConnectDialogOpen] = useState(false);
  const [addEntityDialogOpen, setAddEntityDialogOpen] = useState(false);
  const [editEntityDialogOpen, setEditEntityDialogOpen] = useState(false);
  const [deleteEntityDialogOpen, setDeleteEntityDialogOpen] = useState(false);
  const [selectedEntity, setSelectedEntity] = useState<BillingEntityResponse | null>(null);

  // Form states
  const [apiKey, setApiKey] = useState('');
  const [accountLabel, setAccountLabel] = useState('');
  const [entityFormData, setEntityFormData] = useState<BillingEntityFormData>(initialFormData);
  const [formError, setFormError] = useState<string | null>(null);

  // Loading states
  const [syncingEntityId, setSyncingEntityId] = useState<string | null>(null);
  const [syncingEnrollmentId, setSyncingEnrollmentId] = useState<string | null>(null);
  const [enrollingPayerId, setEnrollingPayerId] = useState<string | null>(null);

  // ============================================================================
  // Data Hooks
  // ============================================================================

  const { status: stediStatus, isConnected, isLoading: stediLoading, refetch: refetchStedi } = useStediStatusAdmin(
    organizationId,
  );
  const { connect, connectLoading, verify, verifyLoading, disconnect, disconnectLoading } = useStediConnectionAdmin();
  const { readiness, isLoading: readinessLoading, refetch: refetchReadiness } = useOrgReadinessAdmin(organizationId);
  const { entities, isLoading: entitiesLoading, refetch: refetchEntities } = useBillingEntitiesAdmin(organizationId);
  const { enrollments, isLoading: enrollmentsLoading, refetch: refetchEnrollments } = useEnrollmentsAdmin(organizationId);
  const {
    create: createEntity,
    createLoading,
    remove: deleteEntity,
    removeLoading,
    sync: syncEntity,
    syncLoading
  } = useBillingEntityOperationsAdmin();
  const { 
    initiate: initiateEnrollment, 
    initiateLoading,
    sync: syncEnrollment, 
    syncAll, 
    syncAllLoading 
  } = useEnrollmentOperationsAdmin();

  // Get default entity for enrollments
  // First try to find explicitly default entity, then fall back to first synced entity
  const defaultEntity = entities.find(e => e.is_default && e.stedi_synced) || 
                        entities.find(e => e.stedi_synced);
  const defaultEntityId = defaultEntity?.billing_entity_id || null;

  const isLoading = stediLoading || readinessLoading || entitiesLoading || enrollmentsLoading;

  const hasConnection = isConnected;
  const hasIdentities = entities.length > 0;
  const hasEnrollments = enrollments.length > 0;
  const hasUnsyncedEntities = entities.some(e => !e.stedi_synced);

  // ============================================================================
  // Handlers
  // ============================================================================

  const handleTabChange = useCallback((_event: React.SyntheticEvent, newValue: TabValue) => {
    setCurrentTab(newValue);
  }, []);

  const handleRefreshAll = useCallback(() => {
    refetchStedi();
    refetchReadiness();
    refetchEntities();
    refetchEnrollments();
  }, [refetchStedi, refetchReadiness, refetchEntities, refetchEnrollments]);

  // Connect to Stedi
  const handleConnect = async () => {
    if (!apiKey.trim()) {
      setFormError('Please enter a Stedi API key');
      return;
    }

    setFormError(null);
    try {
      await connect({
        organizationId,
        connectStediRequest: {
          api_key: apiKey.trim(),
          account_label: accountLabel.trim() || undefined,
        },
      }).unwrap();
      enqueueSnackbar('Connected to Stedi successfully', { variant: 'success' });
      setConnectDialogOpen(false);
      setApiKey('');
      setAccountLabel('');
      refetchStedi();
      refetchReadiness();
    } catch (error) {
      const errorMessage = extractApiErrorMessage(error, 'Failed to connect to Stedi');
      setFormError(errorMessage);
      enqueueSnackbar(errorMessage, { variant: 'error' });
    }
  };

  const handleVerifyConnection = async () => {
    try {
      await verify({ organizationId }).unwrap();
      enqueueSnackbar('Stedi connection verified successfully', { variant: 'success' });
      refetchStedi();
    } catch (error) {
      const errorMessage = extractApiErrorMessage(error, 'Failed to verify Stedi connection');
      enqueueSnackbar(errorMessage, { variant: 'error' });
    }
  };

  const handleDisconnect = async () => {
    try {
      await disconnect({ organizationId }).unwrap();
      enqueueSnackbar('Disconnected from Stedi', { variant: 'success' });
      refetchStedi();
      refetchReadiness();
    } catch (error) {
      const errorMessage = extractApiErrorMessage(error, 'Failed to disconnect from Stedi');
      enqueueSnackbar(errorMessage, { variant: 'error' });
    }
  };

  // Billing Entity CRUD
  const handleOpenAddEntity = () => {
    setEntityFormData(initialFormData);
    setFormError(null);
    setAddEntityDialogOpen(true);
  };

  const handleOpenEditEntity = (entity: BillingEntityResponse) => {
    setSelectedEntity(entity);
    setEditEntityDialogOpen(true);
  };

  const handleOpenDeleteEntity = (entity: BillingEntityResponse) => {
    setSelectedEntity(entity);
    setDeleteEntityDialogOpen(true);
  };

  const handleCreateEntity = async () => {
    setFormError(null);
    try {
      await createEntity({
        organizationId,
        createBillingEntityRequest: {
          billing_npi: entityFormData.billing_npi.replace(/\D/g, ''),
          tax_id: entityFormData.tax_id.replace(/\D/g, ''),
          tax_id_type: entityFormData.tax_id_type,
          legal_name: entityFormData.legal_name.trim(),
          doing_business_as: entityFormData.doing_business_as.trim() || undefined,
          billing_address: {
            line1: entityFormData.billing_address.line1.trim(),
            line2: entityFormData.billing_address.line2.trim() || undefined,
            city: entityFormData.billing_address.city.trim(),
            state: entityFormData.billing_address.state.trim().toUpperCase(),
            zip: entityFormData.billing_address.zip.trim(),
          },
          contact_name: entityFormData.contact_name.trim() || undefined,
          contact_email: entityFormData.contact_email.trim(),
          contact_phone: entityFormData.contact_phone.trim(),
        },
      }).unwrap();
      enqueueSnackbar('Billing identity created successfully', { variant: 'success' });
      setAddEntityDialogOpen(false);
      refetchEntities();
      refetchReadiness();
    } catch (error) {
      const errorMessage = extractApiErrorMessage(error, 'Failed to create billing identity');
      setFormError(errorMessage);
      enqueueSnackbar(errorMessage, { variant: 'error' });
    }
  };

  const handleDeleteEntity = async () => {
    if (!selectedEntity) return;
    try {
      await deleteEntity({
        organizationId,
        billingEntityId: selectedEntity.billing_entity_id,
      }).unwrap();
      enqueueSnackbar('Billing identity deleted successfully', { variant: 'success' });
      setDeleteEntityDialogOpen(false);
      setSelectedEntity(null);
      refetchEntities();
      refetchReadiness();
    } catch (error) {
      const errorMessage = extractApiErrorMessage(error, 'Failed to delete billing identity');
      enqueueSnackbar(errorMessage, { variant: 'error' });
    }
  };

  const handleSyncEntity = async (billingEntityId: string) => {
    setSyncingEntityId(billingEntityId);
    try {
      await syncEntity({ organizationId, billingEntityId }).unwrap();
      enqueueSnackbar('Billing identity verified successfully', { variant: 'success' });
      refetchEntities();
      refetchReadiness();
    } catch (error) {
      const errorMessage = extractApiErrorMessage(error, 'Failed to verify billing identity');
      enqueueSnackbar(errorMessage, { variant: 'error', autoHideDuration: 10000 });
    } finally {
      setSyncingEntityId(null);
    }
  };

  // Enrollment Operations
  const handleInitiateEnrollment = async (payerId: string, transactionType: string, payerName?: string) => {
    if (!defaultEntityId) return;
    setEnrollingPayerId(payerId);
    try {
      await initiateEnrollment({
        organizationId,
        initiateEnrollmentRequest: {
          billing_entity_id: defaultEntityId,
          payer_uuid: payerId,
          transaction_type: transactionType,
        },
      }).unwrap();
      enqueueSnackbar(
        `Enrollment initiated for ${payerName || 'payer'}. Usually takes 1-2 weeks for approval.`,
        { variant: 'success' },
      );
      refetchEnrollments();
    } catch (error) {
      const errorMessage = extractApiErrorMessage(error, 'Failed to initiate enrollment');
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setEnrollingPayerId(null);
    }
  };

  const handleSyncEnrollment = async (enrollmentId: string) => {
    setSyncingEnrollmentId(enrollmentId);
    try {
      await syncEnrollment({ organizationId, enrollmentId }).unwrap();
      enqueueSnackbar('Enrollment status updated', { variant: 'success' });
      refetchEnrollments();
    } catch (error) {
      const errorMessage = extractApiErrorMessage(error, 'Failed to sync enrollment');
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setSyncingEnrollmentId(null);
    }
  };

  const handleSyncAllEnrollments = async () => {
    try {
      await syncAll({ organizationId }).unwrap();
      enqueueSnackbar('All enrollments synced successfully', { variant: 'success' });
      refetchEnrollments();
    } catch (error) {
      const errorMessage = extractApiErrorMessage(error, 'Failed to sync enrollments');
      enqueueSnackbar(errorMessage, { variant: 'error' });
    }
  };

  // Form field change handlers
  const handleEntityFieldChange = (field: keyof BillingEntityFormData) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setEntityFormData(prev => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleAddressFieldChange = (field: keyof BillingEntityFormData['billing_address']) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setEntityFormData(prev => ({
      ...prev,
      billing_address: {
        ...prev.billing_address,
        [field]: event.target.value,
      },
    }));
  };

  // ============================================================================
  // Render
  // ============================================================================

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Claim Submission
        </Typography>
        <Button
          variant="outlined"
          size="small"
          startIcon={<RefreshIcon />}
          onClick={handleRefreshAll}
        >
          Refresh
        </Button>
      </Box>

      {/* Setup Progress Indicator */}
      <SetupProgressIndicator
        hasConnection={hasConnection}
        hasIdentities={hasIdentities}
        allIdentitiesVerified={hasIdentities && !hasUnsyncedEntities}
        hasRequiredEnrollments={hasEnrollments}
      />

      {/* Tabs Navigation */}
      <Paper
        elevation={0}
        sx={{
          mb: 3,
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'transparent',
        }}
      >
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          textColor="primary"
          indicatorColor="primary"
          sx={{
            '& .MuiTabs-indicator': {
              height: 3,
              borderRadius: '3px 3px 0 0',
            },
            '& .MuiTab-root': {
              textTransform: 'none',
              minWidth: 'auto',
              px: 3,
              fontSize: '0.9375rem',
              fontWeight: 500,
              color: theme.palette.text.secondary,
              '&.Mui-selected': {
                color: theme.palette.primary.main,
                fontWeight: 600,
              },
            },
          }}
        >
          <Tab value="overview" label="Overview" />
          <Tab value="identities" label="Billing Identities" />
          <Tab value="enrollments" label="Payer Enrollments" />
        </Tabs>
      </Paper>

      {/* Overview Tab */}
      <TabPanel value="overview" activeTab={currentTab}>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {/* Clearinghouse Connection Status */}
          <Grid item xs={12} md={4}>
            <Card
              elevation={0}
              sx={{
                border: `1px solid ${theme.palette.grey[200]}`,
                borderRadius: 2,
                height: '100%',
              }}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {isConnected ? (
                      <CheckCircleIcon color="success" sx={{ mr: 1 }} />
                    ) : (
                      <ErrorIcon color="error" sx={{ mr: 1 }} />
                    )}
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      Clearinghouse
                    </Typography>
                  </Box>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {isConnected ? 'Connected' : 'Not connected'}
                </Typography>
                {isConnected && stediStatus?.account_label && (
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                    <Chip label={stediStatus.account_label} size="small" color="primary" />
                    <Chip label={stediStatus.mode || 'SHARED'} size="small" variant="outlined" />
                  </Box>
                )}
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {!isConnected ? (
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<PlayArrowIcon />}
                      onClick={() => setConnectDialogOpen(true)}
                    >
                      Connect
                    </Button>
                  ) : (
                    <>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={verifyLoading ? <CircularProgress size={16} /> : <SyncIcon />}
                        onClick={handleVerifyConnection}
                        disabled={verifyLoading}
                      >
                        Verify
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        color="error"
                        onClick={handleDisconnect}
                        disabled={disconnectLoading}
                      >
                        Disconnect
                      </Button>
                    </>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Billing Identities Status */}
          <Grid item xs={12} md={4}>
            <Card
              elevation={0}
              sx={{
                border: `1px solid ${theme.palette.grey[200]}`,
                borderRadius: 2,
                height: '100%',
              }}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  {hasIdentities && !hasUnsyncedEntities ? (
                    <CheckCircleIcon color="success" sx={{ mr: 1 }} />
                  ) : hasIdentities ? (
                    <ErrorIcon color="warning" sx={{ mr: 1 }} />
                  ) : (
                    <ErrorIcon color="error" sx={{ mr: 1 }} />
                  )}
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    Billing Identities
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {hasIdentities
                    ? `${entities.length} ${entities.length === 1 ? 'identity' : 'identities'}${hasUnsyncedEntities ? ' (setup required)' : ''}`
                    : 'None configured'}
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setCurrentTab('identities')}
                >
                  Manage
                </Button>
              </CardContent>
            </Card>
          </Grid>

          {/* Payer Enrollments Status */}
          <Grid item xs={12} md={4}>
            <Card
              elevation={0}
              sx={{
                border: `1px solid ${theme.palette.grey[200]}`,
                borderRadius: 2,
                height: '100%',
              }}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  {hasEnrollments ? (
                    <CheckCircleIcon color="success" sx={{ mr: 1 }} />
                  ) : (
                    <ErrorIcon color="warning" sx={{ mr: 1 }} />
                  )}
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    Payer Enrollments
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {hasEnrollments
                    ? `${enrollments.filter(e => e.status === 'LIVE').length} active`
                    : 'None required'}
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setCurrentTab('enrollments')}
                >
                  Manage
                </Button>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {!hasConnection && !hasIdentities && !hasEnrollments && (
          <Alert severity="info">
            <Typography variant="body2">
              This organization has not configured claim submission yet.
            </Typography>
          </Alert>
        )}
      </TabPanel>

      {/* Billing Identities Tab */}
      <TabPanel value="identities" activeTab={currentTab}>
        <Card
          elevation={0}
          sx={{
            border: `1px solid ${theme.palette.grey[200]}`,
            borderRadius: 2,
          }}
        >
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Billing Identities
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Each identity represents an NPI and Tax ID combination for billing
                </Typography>
              </Box>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleOpenAddEntity}
                disabled={!hasConnection}
              >
                Add Identity
              </Button>
            </Box>

            {!hasConnection && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Connect to Stedi first before adding billing identities.
              </Alert>
            )}

            {entities.length > 0 ? (
              <>
                {hasUnsyncedEntities && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    Some billing identities need to be verified with the clearinghouse.
                  </Alert>
                )}

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {entities.map(entity => (
                    <Box
                      key={entity.billing_entity_id}
                      sx={{
                        p: 2.5,
                        border: '1px solid',
                        borderColor: theme.palette.grey[200],
                        borderRadius: 2,
                        bgcolor: theme.palette.background.paper,
                        '&:hover': {
                          borderColor: theme.palette.grey[300],
                        },
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="subtitle1" fontWeight={600}>
                            {entity.legal_name}
                          </Typography>
                          {entity.doing_business_as && (
                            <Typography variant="body2" color="text.secondary">
                              DBA: {entity.doing_business_as}
                            </Typography>
                          )}
                          <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                            <Typography variant="caption" color="text.secondary">
                              NPI: {entity.billing_npi}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Tax ID: {entity.tax_id_masked} ({entity.tax_id_type})
                            </Typography>
                          </Box>
                          {entity.stedi_provider_id && (
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                              Stedi Provider ID: {entity.stedi_provider_id}
                            </Typography>
                          )}
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {entity.is_default && (
                            <Chip label="Default" size="small" color="primary" variant="outlined" />
                          )}
                          <Chip
                            label={entity.stedi_synced ? 'Ready' : 'Setup Required'}
                            size="small"
                            color={entity.stedi_synced ? 'success' : 'warning'}
                          />

                          {!entity.stedi_synced && (
                            <Tooltip title="Verify billing identity">
                              <span>
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => handleSyncEntity(entity.billing_entity_id)}
                                  disabled={syncLoading || syncingEntityId === entity.billing_entity_id}
                                >
                                  {syncingEntityId === entity.billing_entity_id ? (
                                    <CircularProgress size={20} />
                                  ) : (
                                    <SyncIcon />
                                  )}
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}

                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              onClick={() => handleOpenEditEntity(entity)}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleOpenDeleteEntity(entity)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>

                      {entity.stedi_synced && entity.stedi_synced_at && (
                        <Typography variant="caption" color="success.main" sx={{ mt: 1, display: 'block' }}>
                          Verified: {new Date(entity.stedi_synced_at).toLocaleString()}
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Box>
              </>
            ) : hasConnection ? (
              <Alert severity="info">
                <Typography variant="body2">
                  No billing identities configured. Click "Add Identity" to create one.
                </Typography>
              </Alert>
            ) : null}
          </CardContent>
        </Card>
      </TabPanel>

      {/* Payer Enrollments Tab */}
      <TabPanel value="enrollments" activeTab={currentTab}>
        <Card
          elevation={0}
          sx={{
            border: `1px solid ${theme.palette.grey[200]}`,
            borderRadius: 2,
          }}
        >
          <CardContent>
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Payer Enrollments
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Some insurance companies require enrollment before accepting electronic claims. Search for a payer to check if enrollment is required.
                </Typography>
              </Box>
              {enrollments.length > 0 && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={syncAllLoading ? <CircularProgress size={16} /> : <SyncIcon />}
                  onClick={handleSyncAllEnrollments}
                  disabled={syncAllLoading}
                >
                  {syncAllLoading ? 'Syncing...' : 'Sync All'}
                </Button>
              )}
            </Box>

            {/* Payer Search */}
            <Box sx={{ mb: 3 }}>
              <PayerSearchDropdown
                onEnroll={(payerId, payerName) => handleInitiateEnrollment(payerId, '837P', payerName)}
                isEnrolling={initiateLoading}
                enrollingPayerId={enrollingPayerId}
                placeholder="Search for a payer to start enrollment..."
              />
              {!defaultEntityId && entities.length > 0 && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    Set a default billing identity to enable enrollment. Go to the Billing Identities tab to set one as default.
                  </Typography>
                </Alert>
              )}
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Payer Search */}
            {defaultEntityId && (
              <Box sx={{ mb: 3 }}>
                <PayerSearchDropdown
                  onEnroll={handleInitiateEnrollment}
                  isEnrolling={initiateLoading}
                  enrollingPayerId={enrollingPayerId}
                />
              </Box>
            )}

            <Divider sx={{ my: 3 }} />

            {/* Active Enrollments List */}
            {enrollments.length > 0 && (
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                  My Enrollments {enrollments.length > 0 && `(${enrollments.length})`}
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {enrollments.map(enrollment => {
                    const statusColor = enrollment.status === 'LIVE' ? 'success'
                      : enrollment.status === 'PENDING' || enrollment.status === 'PROVIDER_ACTION_REQUIRED' || enrollment.status === 'STEDI_ACTION_REQUIRED' ? 'warning'
                      : enrollment.status === 'PROVISIONING' || enrollment.status === 'DRAFT' ? 'info'
                      : enrollment.status === 'REJECTED' ? 'error'
                      : 'default';

                    const canSync = enrollment.status !== 'LIVE' && enrollment.status !== 'REJECTED';
                    const isSyncing = syncingEnrollmentId === enrollment.stedi_transaction_enrollment_id;

                    return (
                      <Box
                        key={enrollment.stedi_transaction_enrollment_id}
                        sx={{
                          p: 2,
                          border: `1px solid ${theme.palette.grey[200]}`,
                          borderRadius: 2,
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                        }}
                      >
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            {enrollment.payer_name || enrollment.transaction_type}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {enrollment.transaction_type} • ID: {enrollment.stedi_transaction_enrollment_id}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip
                            label={enrollment.status}
                            size="small"
                            color={statusColor}
                          />
                          {canSync && (
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={isSyncing ? <CircularProgress size={16} /> : <SyncIcon />}
                              onClick={() => handleSyncEnrollment(enrollment.stedi_transaction_enrollment_id)}
                              disabled={isSyncing}
                            >
                              {isSyncing ? 'Syncing...' : 'Sync'}
                            </Button>
                          )}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            )}

            {/* Empty states */}
            {enrollments.length === 0 && (
              <Alert severity="info">
                <Typography variant="body2">
                  No payer enrollments configured. Most claims can be submitted without enrollment. Search for a payer above to check if enrollment is required.
                </Typography>
              </Alert>
            )}

            {entities.length === 0 && (
              <Alert severity="info">
                <Typography variant="body2">
                  No billing identities configured. Add a billing identity first to manage payer enrollments.
                </Typography>
              </Alert>
            )}

            {!hasConnection && (
              <Alert severity="warning">
                <Typography variant="body2">
                  Connect to Stedi first to manage payer enrollments.
                </Typography>
              </Alert>
            )}
          </CardContent>
        </Card>
      </TabPanel>

      {/* ================================================================== */}
      {/* DIALOGS */}
      {/* ================================================================== */}

      {/* Connect to Stedi Dialog */}
      <Dialog open={connectDialogOpen} onClose={() => setConnectDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span>Connect to Stedi</span>
            <IconButton edge="end" onClick={() => setConnectDialogOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Enter the organization's Stedi API key to connect to the clearinghouse.
          </Typography>
          <TextField
            label="Stedi API Key"
            type="password"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            fullWidth
            required
            autoFocus
            sx={{ mb: 2 }}
            helperText="The API key from the Stedi app installation"
          />
          <TextField
            label="Account Label (optional)"
            value={accountLabel}
            onChange={(e) => setAccountLabel(e.target.value)}
            fullWidth
            helperText="For identification, e.g., clinic name"
          />
          {formError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {formError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConnectDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleConnect}
            disabled={connectLoading}
            startIcon={connectLoading ? <CircularProgress size={20} /> : undefined}
          >
            {connectLoading ? 'Connecting...' : 'Connect'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Billing Entity Dialog */}
      <Dialog open={addEntityDialogOpen} onClose={() => setAddEntityDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span>Add Billing Identity</span>
            <IconButton edge="end" onClick={() => setAddEntityDialogOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Billing NPI"
                value={entityFormData.billing_npi}
                onChange={handleEntityFieldChange('billing_npi')}
                fullWidth
                required
                helperText="10-digit National Provider Identifier"
                inputProps={{ maxLength: 10 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Tax ID Type</InputLabel>
                <Select
                  value={entityFormData.tax_id_type}
                  label="Tax ID Type"
                  onChange={e => setEntityFormData(prev => ({ ...prev, tax_id_type: e.target.value }))}
                >
                  <MenuItem value="EIN">EIN (Employer Identification Number)</MenuItem>
                  <MenuItem value="SSN">SSN (Social Security Number)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label={`Tax ID (${entityFormData.tax_id_type})`}
                value={entityFormData.tax_id}
                onChange={handleEntityFieldChange('tax_id')}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Legal Name"
                value={entityFormData.legal_name}
                onChange={handleEntityFieldChange('legal_name')}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Doing Business As (DBA)"
                value={entityFormData.doing_business_as}
                onChange={handleEntityFieldChange('doing_business_as')}
                fullWidth
                helperText="Optional"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Address Line 1"
                value={entityFormData.billing_address.line1}
                onChange={handleAddressFieldChange('line1')}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Address Line 2"
                value={entityFormData.billing_address.line2}
                onChange={handleAddressFieldChange('line2')}
                fullWidth
                helperText="Optional"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="City"
                value={entityFormData.billing_address.city}
                onChange={handleAddressFieldChange('city')}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="State"
                value={entityFormData.billing_address.state}
                onChange={handleAddressFieldChange('state')}
                fullWidth
                required
                helperText="2-letter code"
                inputProps={{ maxLength: 2 }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="ZIP Code"
                value={entityFormData.billing_address.zip}
                onChange={handleAddressFieldChange('zip')}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                Contact Information
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Contact Name"
                value={entityFormData.contact_name}
                onChange={handleEntityFieldChange('contact_name')}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Contact Email"
                type="email"
                value={entityFormData.contact_email}
                onChange={handleEntityFieldChange('contact_email')}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Contact Phone"
                value={entityFormData.contact_phone}
                onChange={handleEntityFieldChange('contact_phone')}
                fullWidth
                required
              />
            </Grid>
          </Grid>
          {formError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {formError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddEntityDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreateEntity}
            disabled={createLoading}
            startIcon={createLoading ? <CircularProgress size={20} /> : undefined}
          >
            {createLoading ? 'Creating...' : 'Create Identity'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Billing Entity Dialog (shared modal) */}
      {selectedEntity && (
        <EditBillingEntityModal
          open={editEntityDialogOpen}
          onClose={() => {
            setEditEntityDialogOpen(false);
            setSelectedEntity(null);
          }}
          entity={selectedEntity}
          organizationId={organizationId}
          onUpdated={() => {
            refetchEntities();
            refetchReadiness();
          }}
        />
      )}

      {/* Delete Billing Entity Dialog */}
      <Dialog open={deleteEntityDialogOpen} onClose={() => setDeleteEntityDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Delete Billing Identity</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the billing identity "{selectedEntity?.legal_name}"?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteEntityDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteEntity}
            disabled={removeLoading}
            startIcon={removeLoading ? <CircularProgress size={20} /> : undefined}
          >
            {removeLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
